import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertBanner, PopoverMenu, SparkIcon } from '@spark';

const MENU_GAP = 4;

export const STALE_BANNER_TEXT = {
    prompt: 'Prompt updated. These results are from the previous version.',
    inputs: 'Inputs updated. These results are from the previous version.',
    model: 'Model updated. These results are from the previous version.',
};

const STALE_ORDER = ['prompt', 'inputs', 'model'];

export const unknownFieldWarningText = (modelName, field) =>
    `${modelName}'s prompt uses {{${field}}}, which isn't a field in this dataset. The model gets it as plain text.`;

/** "Fix" pill (Figma 2218:93308) opening the three repair options (2218:146296). */
const FixMenu = ({ field, onAddField, onRemoveField, onIgnore }) => {
    const [open, setOpen] = useState(false);
    const [panelStyle, setPanelStyle] = useState({});
    const triggerRef = useRef(null);
    const panelRef = useRef(null);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;
        const rect = trigger.getBoundingClientRect();
        setPanelStyle({
            position: 'fixed',
            top: rect.bottom + MENU_GAP,
            right: Math.max(8, window.innerWidth - rect.right),
            zIndex: 1400,
        });
    }, []);

    useLayoutEffect(() => {
        if (!open) return undefined;
        updatePosition();
        const id = requestAnimationFrame(updatePosition);
        return () => cancelAnimationFrame(id);
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return undefined;
        const handleReposition = () => updatePosition();
        const handleOutside = (e) => {
            if (triggerRef.current?.contains(e.target)) return;
            if (panelRef.current?.contains(e.target)) return;
            setOpen(false);
        };
        const handleKey = (e) => {
            if (e.key === 'Escape') setOpen(false);
        };
        window.addEventListener('scroll', handleReposition, true);
        window.addEventListener('resize', handleReposition);
        document.addEventListener('mousedown', handleOutside);
        document.addEventListener('keydown', handleKey);
        return () => {
            window.removeEventListener('scroll', handleReposition, true);
            window.removeEventListener('resize', handleReposition);
            document.removeEventListener('mousedown', handleOutside);
            document.removeEventListener('keydown', handleKey);
        };
    }, [open, updatePosition]);

    const pick = (action) => () => {
        setOpen(false);
        action?.();
    };

    const items = [
        { label: 'Add it as a field in Model Configuration', onClick: pick(onAddField) },
        { label: `Remove {{${field}}}`, onClick: pick(onRemoveField) },
        { label: 'Ignore', onClick: pick(onIgnore) },
    ];

    return (
        <>
            <button
                ref={triggerRef}
                type="button"
                className="spark-alert-banner-action pg-banner__fix"
                aria-haspopup="menu"
                aria-expanded={open}
                onClick={() => setOpen((value) => !value)}
            >
                Fix
                <SparkIcon slug="chevron-down" size="small" />
            </button>
            {open
                ? createPortal(
                    <div
                        ref={panelRef}
                        style={panelStyle}
                        className="pg-banner__fix-menu"
                        data-testid="pg-fix-menu"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <PopoverMenu tip={false} items={items} />
                    </div>,
                    document.body
                )
                : null}
        </>
    );
};

/**
 * Banners between the examples toolbar and the rows (Figma 2218:93290 /
 * 2171:86207):
 * - one warning per `{{field}}` a model's prompt uses that the dataset
 *   does not have, with a Fix menu (add it in Model Configuration / remove
 *   the token / ignore — closing the banner also ignores it);
 * - one info banner per reason results went stale (prompt / inputs /
 *   model), with "Run again".
 */
const PlaygroundBanners = ({
    slots = [],
    fieldWarnings = [],
    staleReasons = {},
    isRunning = false,
    runDisabled = false,
    onAddField,
    onRemoveField,
    onIgnoreWarning,
    onRunAgain,
}) => {
    const [dismissedStale, setDismissedStale] = useState(() => new Set());

    const staleKeys = useMemo(
        () => Object.entries(staleReasons).map(([slotId, reason]) => `${slotId}:${reason}`),
        [staleReasons]
    );

    // A banner closed for one stale state comes back if the slot goes stale
    // again later (after a re-run), so forget keys that are no longer stale.
    useEffect(() => {
        setDismissedStale((prev) => {
            const live = new Set(staleKeys);
            const next = new Set([...prev].filter((key) => live.has(key)));
            return next.size === prev.size ? prev : next;
        });
    }, [staleKeys]);

    const staleBanners = STALE_ORDER.map((reason) => {
        const slotIds = Object.entries(staleReasons)
            .filter(([, value]) => value === reason)
            .map(([slotId]) => slotId);
        const visible = slotIds.filter((slotId) => !dismissedStale.has(`${slotId}:${reason}`));
        return visible.length > 0 ? { reason, slotIds: visible } : null;
    }).filter(Boolean);

    const nameOf = (slotId) => slots.find((slot) => slot.slotId === slotId)?.displayName || 'This model';

    if (fieldWarnings.length === 0 && staleBanners.length === 0) return null;

    return (
        <div className="pg-banners" data-testid="pg-banners">
            {fieldWarnings.map(({ slotId, field }) => (
                <AlertBanner
                    key={`${slotId}:${field}`}
                    className="pg-banner pg-banner--warning"
                    feedback="warning"
                    text={unknownFieldWarningText(nameOf(slotId), field)}
                    action={
                        <FixMenu
                            field={field}
                            onAddField={onAddField}
                            onRemoveField={() => onRemoveField?.(slotId, field)}
                            onIgnore={() => onIgnoreWarning?.(slotId, field)}
                        />
                    }
                    onCloseClick={() => onIgnoreWarning?.(slotId, field)}
                />
            ))}
            {staleBanners.map(({ reason, slotIds }) => (
                <AlertBanner
                    key={reason}
                    className="pg-banner pg-banner--info"
                    feedback="info"
                    text={STALE_BANNER_TEXT[reason]}
                    action={
                        <button
                            type="button"
                            className="spark-alert-banner-action pg-banner__run-again"
                            disabled={isRunning || runDisabled}
                            onClick={onRunAgain}
                        >
                            Run again
                        </button>
                    }
                    onCloseClick={() =>
                        setDismissedStale((prev) => {
                            const next = new Set(prev);
                            slotIds.forEach((slotId) => next.add(`${slotId}:${reason}`));
                            return next;
                        })
                    }
                />
            ))}
        </div>
    );
};

export default PlaygroundBanners;
